import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { RuntimeProvider, useLive, useRuntime, useUser } from './context';
import type { Runtime } from '../runtime';
import { StatusStrip } from './StatusStrip';
import { SignIn } from './SignIn';
import { Today } from './Today';
import { CalendarView } from './CalendarView';
import { Clients } from './Clients';
import { Newsletter } from './Newsletter';
import { Reports } from './Reports';
import { Settings } from './Settings';
import { Team } from './Team';
import { Tasks } from './Tasks';
import { Orders } from './Orders';
import { Reservations } from './Reservations';
import { Parcels } from './Parcels';
import { Departures } from './Departures';
import { FrontDesk } from './FrontDesk';
import { Albums } from './Albums';
import { GiftCards } from './Certificates';
import { useModules, useStaffBranding } from './useRemote';
import type { TenantFacts } from '../mirror/mirror';
import { applyStaffBranding } from '../theme';
import { useT } from '../messages';
import type { Messages, T } from '../messages';

/**
 * The shell: LoveSpa's shape, because that shape works on an iPad behind a
 * spotty connection (spec §6.2).
 *
 * Tabs rather than a router, and no router dependency. The app has five screens
 * and no deep links — an installed PWA on a counter is opened by tapping its
 * icon, not by pasting a URL — so a route table would be a dependency and a
 * bundle for a `useState`.
 *
 * **Tasks is a tab, not a modal.** §8.3's rejected intents are the owner's work
 * queue, and burying them behind a toast that can be missed would be the same
 * mistake as merging them: the honest cost of the model has to be somewhere the
 * owner can see it, count it, and come back to it.
 *
 * **MVP 4 added one tab, not four.** Reports is its own screen because it is its
 * own activity — the owner sits down with it, rather than reaching for it
 * between guests. The rest of the money went where the work already happens:
 * checkout is a sheet over Today (the visit is right there), the retail catalog
 * is a panel in Settings beside the menu it belongs to, and account credit is
 * on the guest's own record. A "Money" tab would have collected four unrelated
 * jobs under one word and put each of them one tap further from its context.
 *
 * **Gift cards left Settings for a tab of their own** (owner's decision 1 of
 * 2026-09-23). The argument above placed them beside the menu because issuing
 * one looked like catalog work; a month at the counter said otherwise. Selling
 * a gift card is a *sale* — somebody at the desk, a payment taken, a card
 * handed over — and looking one up is what she does with a guest in front of
 * her. Her old app had the tab, she reached for it, and Settings is the one
 * screen whose job is to be visited rarely. It is still not a "Money" tab: it
 * holds one job, under the word she uses for it.
 */

/**
 * **The vertical screens are tabs the tenant either has or does not see.** A
 * restaurant gets Orders, a dive shop gets Departures, a guesthouse gets Front
 * desk, and a spa gets none of the three — because `session.modules` says so,
 * and that list is spec §3.1's table crossed with what the tenant bought rather
 * than anything this file decides. Hidden, not disabled: §3.2's own argument for
 * answering `404` instead of `501` is that we do not advertise what a tenant has
 * not got, and a greyed-out tab is an advertisement.
 */
export type Tab =
  | 'today'
  | 'calendar'
  | 'orders'
  | 'reservations'
  | 'parcels'
  | 'departures'
  | 'frontdesk'
  | 'albums'
  | 'clients'
  | 'giftcards'
  | 'newsletter'
  | 'team'
  | 'tasks'
  | 'reports'
  | 'settings';

export interface TabSpec {
  id: Tab;
  /**
   * The English name, and **not what is painted.**
   *
   * The row stays the row: `visibleTabs` is tested on it and an operator reading
   * this file should be able to see what each tab is without opening a catalog.
   * What the shell renders is `t(labelKey(spec.id))` — translated at render, out
   * of `messages/en.ts`'s `tabs.*` — because a language baked into a module
   * constant is a language decided before the app knows who is holding it.
   */
  label: string;
  /** `undefined` is a platform tab: every tenant has it. */
  module?: string;
  /**
   * **A fact the tab also waits for**, beyond its module (`TenantFacts`,
   * `Mirror.refreshTenantFacts`).
   *
   * The module says the tenant *may* use the screen; this says the screen has
   * something to be about yet. Hidden, not disabled — the vertical tabs'
   * argument below — and hidden while the answer is unknown, because a tab
   * that appears a sync late is a smaller surprise than one that appears and
   * then goes away.
   */
  when?: keyof TenantFacts;
}

/**
 * `Tab` → catalog key.
 *
 * `satisfies` rather than an annotation, so the keys stay literal — that is what
 * lets `t` know each of them takes no parameters — while `Record<Tab, …>` still
 * makes a new tab without a label a compile error.
 */
const TAB_LABELS = {
  today: 'tabs.today',
  calendar: 'tabs.calendar',
  orders: 'tabs.orders',
  reservations: 'tabs.reservations',
  parcels: 'tabs.parcels',
  departures: 'tabs.departures',
  frontdesk: 'tabs.frontdesk',
  albums: 'tabs.albums',
  clients: 'tabs.clients',
  giftcards: 'tabs.giftcards',
  newsletter: 'tabs.newsletter',
  team: 'tabs.team',
  tasks: 'tabs.tasks',
  reports: 'tabs.reports',
  settings: 'tabs.settings',
} as const satisfies Record<Tab, keyof Messages>;

export function tabLabel(t: T, id: Tab): string {
  return t(TAB_LABELS[id]);
}

/**
 * The whole navigation, as data — the same shape `core-api`'s route registry
 * has, and for the same reason: a tab whose module is a field can be tested
 * without rendering an app, and a new vertical is a row rather than an edit to
 * a JSX list where the gate is easy to forget.
 */
export const TABS: readonly TabSpec[] = [
  { id: 'today', label: 'Today' },
  { id: 'calendar', label: 'Calendar' },
  { id: 'orders', label: 'Orders', module: 'ordering' },
  /*
   * The shelf — retail's own promise (C1). `products`, not `ordering`. And only
   * once the shop has had a reservation (owner's decision 4 of 2026-09-23): a
   * studio that sells jars at the counter and has no online shop yet opened an
   * empty screen every morning. Latched — once one has happened, the tab stays.
   */
  {
    id: 'reservations',
    label: 'Reservations',
    module: 'products',
    when: 'hadReservation',
  },
  { id: 'parcels', label: 'Parcels', module: 'fulfillment' },
  { id: 'departures', label: 'Departures', module: 'departures' },
  { id: 'frontdesk', label: 'Front desk', module: 'lodging' },
  { id: 'albums', label: 'Albums', module: 'photos' },
  { id: 'clients', label: 'Clients' },
  /*
   * Gift cards (owner's decision 1 of 2026-09-23) — the certificates module
   * (spec §7.3), moved out of Settings. Next to Clients because the two are the
   * desk's people-facing lists: who comes here, and who has been given a visit.
   */
  { id: 'giftcards', label: 'Gift cards', module: 'certificates' },
  /*
   * E3. Module `clients`, matching every newsletter route in the registry:
   * writing to the people who asked to hear from you is the other half of
   * keeping a book of them. And only once `GET /v1/newsletter/sender` says the
   * sender is configured — a composer that cannot send is an evening's writing
   * with nowhere to go. Not latched: configuration can be withdrawn, and the
   * tab follows the latest answer.
   */
  {
    id: 'newsletter',
    label: 'Newsletter',
    module: 'clients',
    when: 'newsletterConfigured',
  },
  /**
   * S2 — the team. `calendar`, matching every resource, hours and blackout row
   * in `core-api`'s registry: a tenant with no calendar has nobody to schedule
   * and the routes behind this screen would all answer `404`.
   *
   * A tab rather than a Settings panel, and it is the one place this wave
   * disagrees with the MVP 4 note above about where a job belongs. The reason is
   * that the *other* things in Settings are the owner's menu and her week — the
   * shop's own facts — and this is a list of **people**, which is the noun the
   * Clients tab next to it is also about. It stays quiet for a solo owner by
   * being quiet, not by being hidden: hiding it would mean the first thing she
   * has to find the day she hires somebody is a tab that is not there.
   */
  { id: 'team', label: 'Team', module: 'calendar' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'reports', label: 'Reports' },
  { id: 'settings', label: 'Settings' },
];

/**
 * The tabs this tenant sees: its modules, and — for a tab that waits on one —
 * the fact it waits for. `facts` defaults to "nothing known", which is what a
 * fresh device has and what hides every waiting tab.
 */
export function visibleTabs(
  modules: readonly string[],
  facts: Partial<TenantFacts> = {},
): TabSpec[] {
  return TABS.filter(
    (tab) =>
      (tab.module === undefined || modules.includes(tab.module)) &&
      (tab.when === undefined || facts[tab.when] === true),
  );
}

const NO_FACTS: TenantFacts = { hadReservation: false, newsletterConfigured: false };

export function App({ runtime }: { runtime: Runtime }): ReactNode {
  return (
    <RuntimeProvider runtime={runtime}>
      <Authenticated />
    </RuntimeProvider>
  );
}

function Authenticated(): ReactNode {
  const runtime = useRuntime();
  const user = useUser();

  useEffect(() => {
    if (user === null) {
      runtime.sync.stop();
      return;
    }
    runtime.sync.start();

    // The radio came back. `online` is a hint rather than a guarantee — a
    // captive portal fires it too — so it only shortcuts the backoff; the cycle
    // itself decides whether there is really a server there.
    const wake = (): void => runtime.sync.wake();
    globalThis.addEventListener?.('online', wake);
    return () => {
      globalThis.removeEventListener?.('online', wake);
    };
  }, [runtime, user]);

  /**
   * **The support look ends when its token does** (`auth/support.ts`).
   *
   * The provider stops presenting the token at `expiresAt`, so requests would
   * start answering `401` and the sync loop would sit in "offline" — honest,
   * but a screen that says *Offline* over a book that is really *closed* is
   * exactly the "looks the same whether it is the truth or a memory" failure
   * §8.1 is a reaction to. So the shell watches the clock itself and signs out
   * at the minute: the mirror is cleared, and the ended screen says what
   * happened in a sentence.
   */
  const support = runtime.supportSession();
  const supportEndsAt = support === null || support.ended ? null : support.expiresAt;
  useEffect(() => {
    if (supportEndsAt === null) return;
    const ms = Math.max(0, Date.parse(supportEndsAt) - runtime.now());
    const timer = setTimeout(() => void runtime.signOut(), ms);
    return () => clearTimeout(timer);
  }, [runtime, supportEndsAt]);

  if (user === null) {
    return support === null ? <SignIn auth={runtime.auth} /> : <SupportEnded />;
  }
  return <Shell />;
}

/**
 * What a support look shows once it is over: not a sign-in form (this seat
 * cannot sign in — the console is the sign-in) and not a blank page. One
 * sentence, and the way to another look, which is another audit row.
 */
function SupportEnded(): ReactNode {
  const t = useT();
  return (
    <main className="app__body">
      <section className="panel" role="status">
        <h2>{t('status.supportEnded')}</h2>
        <p>{t('status.supportEndedHint')}</p>
      </section>
    </main>
  );
}

function Shell(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const [tab, setTab] = useState<Tab>('today');
  const taskCount = useLive(async () => (await runtime.outbox.rejected()).length, 0);
  const modules = useModules();
  const facts = useLive(() => runtime.mirror.tenantFacts(), NO_FACTS);

  /**
   * The named set, painted onto `<html>` (`docs/lovespa-personality.md`).
   *
   * Inside `Shell` rather than `App`, because it is the signed-in tenant's
   * theme: the sign-in screen belongs to *this product* and paints in its
   * colours, and a device that signs out — or signs into a second tenant —
   * lands back on the default because `applyStaffBranding` clears what it set.
   * It reads the mirror, so the branding survives a tunnel with the book.
   */
  const branding = useStaffBranding();
  useEffect(() => {
    applyStaffBranding(document.documentElement, branding);
  }, [branding]);

  const tabs = visibleTabs(modules, facts);
  // A tenant whose module was switched off while the tab was open falls back to
  // Today rather than rendering a screen whose every read now answers `404`.
  const current: Tab = tabs.some((spec) => spec.id === tab) ? tab : 'today';

  return (
    <div className="app">
      <StatusStrip onSignOut={() => void runtime.signOut()} />

      <main className="app__body">
        {current === 'today' ? <Today /> : null}
        {current === 'calendar' ? <CalendarView /> : null}
        {current === 'orders' ? <Orders /> : null}
        {current === 'reservations' ? <Reservations /> : null}
        {current === 'parcels' ? <Parcels /> : null}
        {current === 'departures' ? <Departures /> : null}
        {current === 'frontdesk' ? <FrontDesk /> : null}
        {current === 'albums' ? <Albums /> : null}
        {current === 'clients' ? <Clients /> : null}
        {current === 'giftcards' ? <GiftCards /> : null}
        {current === 'newsletter' ? <Newsletter /> : null}
        {current === 'team' ? <Team /> : null}
        {current === 'tasks' ? <Tasks /> : null}
        {current === 'reports' ? <Reports /> : null}
        {current === 'settings' ? <Settings /> : null}
      </main>

      <nav className="tabs" aria-label={t('tabs.navLabel')}>
        {tabs.map((spec) => (
          <TabButton
            key={spec.id}
            current={current}
            id={spec.id}
            label={tabLabel(t, spec.id)}
            onPick={setTab}
            {...(spec.id === 'tasks' ? { count: taskCount } : {})}
          />
        ))}
      </nav>
    </div>
  );
}

function TabButton({
  current,
  id,
  label,
  onPick,
  count,
}: {
  current: Tab;
  id: Tab;
  label: string;
  onPick: (tab: Tab) => void;
  count?: number;
}): ReactNode {
  return (
    <button
      type="button"
      className={`tabs__tab${current === id ? ' is-current' : ''}`}
      aria-current={current === id ? 'page' : undefined}
      onClick={() => onPick(id)}
    >
      {label}
      {count !== undefined && count > 0 ? <span className="tabs__count">{count}</span> : null}
    </button>
  );
}
